import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Search, Upload, X, Loader2, Download,
  Image as ImageIcon, Video, Music, Layers, RefreshCw,
  Zap, Clock, AlertCircle, Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import ModuleDemoVideo from '../components/ModuleDemoVideo';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'text_to_image',  label: 'Text to Image',  icon: ImageIcon, color: 'from-violet-500 to-fuchsia-500' },
  { id: 'image_to_image', label: 'Image Edit',      icon: Layers,    color: 'from-pink-500 to-rose-500' },
  { id: 'text_to_video',  label: 'Text to Video',   icon: Video,     color: 'from-cyan-500 to-blue-500' },
  { id: 'image_to_video', label: 'Image to Video',  icon: Video,     color: 'from-teal-500 to-emerald-500' },
  { id: 'text_to_audio',  label: 'Audio Studio',    icon: Music,     color: 'from-amber-500 to-orange-500' },
];

const ASPECT_RATIOS = ['1:1', '16:9', '9:16', '4:3', '3:2'];
// Image-to-video models only accept these aspect ratios
const I2V_ASPECT_RATIOS = ['16:9', '9:16'];
// Image-to-video models only accept these durations  
const I2V_DURATIONS = ['5', '8'];
const FREE_MODEL_IDS = new Set(['nano-banana-2', 'nano-banana', 'gpt-image-1', 'sora-2', 'openai-sora-2-text-to-video']);

// Priority models to show first per category
const PRIORITY_MODELS = {
  text_to_audio: 'mmaudio-v2-text-to-audio',
  image_to_video: 'wan2.2-image-to-video',
};

const SUNO_STYLES = ['pop', 'rock', 'jazz', 'classical', 'hip-hop', 'electronic', 'r&b', 'country', 'folk', 'ambient'];
const MINIMAX_VOICES = ['Friendly_Person', 'Wise_Woman', 'Deep_Voice_Man', 'Lively_Girl', 'Patient_Man', 'News_Anchor', 'Energetic_Boy', 'Gentle_Woman'];

export default function MediaStudioPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const [activeCategory, setActiveCategory] = useState('text_to_image');
  const [allModels, setAllModels] = useState({});
  const [selectedModel, setSelectedModel] = useState(null);
  const [modelSearch, setModelSearch] = useState('');
  const [showFreeOnly, setShowFreeOnly] = useState(false);

  const [prompt, setPrompt] = useState('');
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [duration, setDuration] = useState('5');
  const [musicStyle, setMusicStyle] = useState('pop');
  const [voiceId, setVoiceId] = useState('Friendly_Person');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedUrl, setUploadedUrl] = useState('');
  const [uploading, setUploading] = useState(false);

  const [generating, setGenerating] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [pollStatus, setPollStatus] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/api/muapi/models`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        setAllModels(data.models || {});
      } catch (e) {
        toast.error('Failed to load models');
      }
    };
    load();
  }, [token]);

  useEffect(() => {
    const models = allModels[activeCategory] || [];
    const priority = PRIORITY_MODELS[activeCategory];
    const sorted = [...models].sort((a, b) => {
      if (b.free !== a.free) return (b.free ? 1 : 0) - (a.free ? 1 : 0);
      if (priority) {
        if (a.id === priority) return -1;
        if (b.id === priority) return 1;
      }
      return 0;
    });
    setSelectedModel(sorted[0] || null);
    setResult(null);
    setError(null);
    setUploadedFile(null);
    setUploadedUrl('');
    setPrompt('');
    // Set sensible defaults per category
    setAspectRatio(['text_to_video','image_to_video','video_to_video'].includes(activeCategory) ? '16:9' : '1:1');
    setDuration(['image_to_video'].includes(activeCategory) ? '5' : '5');
    if (pollRef.current) clearInterval(pollRef.current);
  }, [activeCategory, allModels]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const filteredModels = (() => {
    const priority = PRIORITY_MODELS[activeCategory];
    return (allModels[activeCategory] || [])
      .filter(m => {
        const matchSearch = m.name.toLowerCase().includes(modelSearch.toLowerCase()) ||
          m.id.toLowerCase().includes(modelSearch.toLowerCase());
        const matchFree = !showFreeOnly || m.free;
        return matchSearch && matchFree;
      })
      .sort((a, b) => {
        if (b.free !== a.free) return (b.free ? 1 : 0) - (a.free ? 1 : 0);
        if (priority) {
          if (a.id === priority) return -1;
          if (b.id === priority) return 1;
        }
        return 0;
      });
  })();

  const needsImage = ['image_to_image', 'image_to_video'].includes(activeCategory);
  const isI2V = activeCategory === 'image_to_video';
  const isVideoOutput = ['text_to_video', 'image_to_video'].includes(activeCategory);
  const isAudioOutput = activeCategory === 'text_to_audio';
  const isFreeModel = selectedModel?.free || FREE_MODEL_IDS.has(selectedModel?.id);
  const modelInputs = selectedModel?.inputs || [];

  const handleFileUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setUploadedFile(file);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/api/muapi/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (data.url) {
        setUploadedUrl(data.url);
        toast.success('File uploaded');
      } else throw new Error(data.detail || 'Upload failed');
    } catch (e) {
      toast.error(`Upload failed: ${e.message}`);
      setUploadedFile(null);
    } finally {
      setUploading(false);
    }
  };

  const saveActivity = async (outputUrl) => {
    try {
      const contentType = isVideoOutput ? 'video' : isAudioOutput ? 'audio' : 'image';
      await fetch(`${API}/api/ai-tools/save-activity`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          tool_name: 'media_studio',
          input_data: {
            prompt: prompt.trim(),
            model_id: selectedModel?.id,
            model_name: selectedModel?.name,
            category: activeCategory,
          },
          output_data: { url: outputUrl },
          content_type: contentType,
        }),
      });
    } catch (_) { /* history save must not break main flow */ }
  };

  const startPolling = (reqId, isFree) => {
    setJobId(reqId);
    setPollStatus('processing');
    const statusEndpoint = isFree ? `/api/muapi/status-free/${reqId}` : `/api/muapi/status/${reqId}`;
    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts++;
      if (attempts > 200) {
        clearInterval(pollRef.current);
        setGenerating(false);
        setError('Generation timed out. Please try again.');
        return;
      }
      try {
        const res = await fetch(`${API}${statusEndpoint}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (data.status === 'completed') {
          clearInterval(pollRef.current);
          setGenerating(false);
          setResult({ output_url: data.output_url });
          toast.success('Generation complete!');
          if (data.output_url) saveActivity(data.output_url);
        } else if (data.status === 'failed') {
          clearInterval(pollRef.current);
          setGenerating(false);
          setError(data.error || 'Generation failed');
          toast.error('Generation failed');
        } else {
          setPollStatus(data.status || 'processing');
        }
      } catch (_) {}
    }, 3000);
  };

  const handleGenerate = async () => {
    if (!selectedModel) { toast.error('Select a model'); return; }
    if (needsImage && !uploadedUrl) { toast.error('Upload an image first'); return; }
    if (!needsImage && !prompt.trim()) { toast.error('Enter a prompt'); return; }
    // Image-to-video always needs a motion prompt to describe the animation
    if (isI2V && !prompt.trim()) { toast.error('Enter a motion prompt (describe how the image should move)'); return; }

    if (pollRef.current) clearInterval(pollRef.current);
    setGenerating(true);
    setResult(null);
    setError(null);
    setPollStatus('submitting');

    const payload = {};
    if (prompt.trim()) payload.prompt = prompt.trim();
    if (aspectRatio && ['text_to_image', 'image_to_image', 'text_to_video', 'image_to_video'].includes(activeCategory)) {
      // For i2v, clamp to only supported ratios (16:9 or 9:16)
      const safeRatio = isI2V ? (aspectRatio === '9:16' ? '9:16' : '16:9') : aspectRatio;
      payload.aspect_ratio = safeRatio;
    }
    if (isVideoOutput && duration) {
      // For i2v, clamp to 5 or 8 (what most MuAPI i2v models accept)
      const safeDur = isI2V ? (parseInt(duration, 10) >= 8 ? 8 : 5) : parseInt(duration, 10);
      payload.duration = safeDur;
    }

    // Audio-specific fields
    if (activeCategory === 'text_to_audio') {
      const modelInputs = selectedModel?.inputs || [];
      if (modelInputs.includes('style')) payload.style = musicStyle;
      if (modelInputs.includes('voice_id')) payload.voice_id = voiceId;
      if (modelInputs.includes('duration')) payload.duration = 10;
    }

    if (needsImage && uploadedUrl) {
      const field = selectedModel?.imageField || 'image_url';
      // Some models expect array, others expect string
      payload[field] = field === 'images_list' ? [uploadedUrl] : uploadedUrl;
    }

    const endpoint = isFreeModel ? '/api/muapi/generate-free' : '/api/muapi/generate';

    try {
      const res = await fetch(`${API}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ model_id: selectedModel.id, payload }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');

      if (data.status === 'completed') {
        setGenerating(false);
        setResult({ output_url: data.output_url });
        toast.success('Done!');
        if (data.output_url) saveActivity(data.output_url);
      } else if (data.request_id) {
        startPolling(data.request_id, isFreeModel);
      } else {
        throw new Error('No request ID returned');
      }
    } catch (e) {
      setGenerating(false);
      setError(e.message);
      toast.error(e.message);
    }
  };

  const downloadResult = (url) => {
    const a = document.createElement('a');
    const fullUrl = url.startsWith('http') ? url : `${API}${url}`;
    a.href = fullUrl;
    a.download = `snagcart-${selectedModel?.id}-${Date.now()}.${isVideoOutput ? 'mp4' : isAudioOutput ? 'mp3' : 'png'}`;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const resultUrl = result?.output_url
    ? (result.output_url.startsWith('http') ? result.output_url : `${API}${result.output_url}`)
    : null;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header */}
      <div className="border-b border-white/10 bg-black/40 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-zinc-400 hover:text-white">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="text-base font-bold text-white">Media Studio</h1>
              <p className="text-xs text-zinc-500">230+ models — some free via Snagcart</p>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded-full border border-emerald-500/30 flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Free models available
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <ModuleDemoVideo moduleKey="media-studio" className="mb-6" />
        {/* Category Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-2 mb-6 scrollbar-hide">
          {CATEGORIES.map(cat => {
            const Icon = cat.icon;
            const isActive = activeCategory === cat.id;
            const count = (allModels[cat.id] || []).length;
            const freeCount = (allModels[cat.id] || []).filter(m => m.free).length;
            return (
              <button
                key={cat.id}
                data-testid={`category-${cat.id}`}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                  isActive
                    ? `bg-gradient-to-r ${cat.color} text-white shadow-lg`
                    : 'bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-white'
                }`}
              >
                <Icon className="w-4 h-4" />
                {cat.label}
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${isActive ? 'bg-white/20' : 'bg-white/10'}`}>{count}</span>
                {freeCount > 0 && (
                  <span className="text-xs px-1.5 py-0.5 rounded-full bg-emerald-500/30 text-emerald-300">{freeCount} free</span>
                )}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
          {/* Model Sidebar */}
          <div className="bg-zinc-900/50 rounded-2xl border border-white/5 p-4 h-fit lg:sticky lg:top-20">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Select Model</p>
              <button
                onClick={() => setShowFreeOnly(v => !v)}
                className={`text-xs px-2 py-1 rounded-full transition-all ${showFreeOnly ? 'bg-emerald-500/30 text-emerald-300' : 'bg-white/5 text-zinc-500 hover:text-white'}`}
              >
                Free only
              </button>
            </div>
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
              <input
                value={modelSearch}
                onChange={e => setModelSearch(e.target.value)}
                placeholder="Search models..."
                className="w-full bg-black/30 border border-white/10 rounded-lg pl-8 pr-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-violet-500/50"
              />
            </div>
            <div className="space-y-1 max-h-[55vh] overflow-y-auto pr-1">
              {filteredModels.length === 0 ? (
                <p className="text-xs text-zinc-600 text-center py-4">No models found</p>
              ) : filteredModels.map(model => (
                <button
                  key={model.id}
                  data-testid={`model-${model.id}`}
                  onClick={() => { setSelectedModel(model); setResult(null); setError(null); }}
                  className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-all ${
                    selectedModel?.id === model.id
                      ? 'bg-violet-500/20 border border-violet-500/30 text-white'
                      : 'hover:bg-white/5 text-zinc-400 hover:text-white border border-transparent'
                  }`}
                >
                  <div className="flex items-center justify-between gap-1">
                    <p className="font-medium truncate flex-1">{model.name}</p>
                    {model.free && (
                      <span className="text-xs px-1.5 py-0.5 bg-emerald-500/20 text-emerald-400 rounded-full shrink-0">Free</span>
                    )}
                  </div>
                  {model.id !== model.name?.toLowerCase().replace(/\s/g,'-') && (
                    <p className="text-xs text-zinc-600 truncate mt-0.5">{model.id}</p>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Right Panel */}
          <div className="space-y-5">
            {selectedModel && (
              <div className="bg-zinc-900/50 rounded-2xl border border-white/5 p-5">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h2 className="text-lg font-bold text-white">{selectedModel.name}</h2>
                      {isFreeModel ? (
                        <span className="text-xs px-2 py-0.5 bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 rounded-full flex items-center gap-1">
                          <Sparkles className="w-3 h-3" /> Free
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 bg-amber-500/20 text-amber-400 border border-amber-500/30 rounded-full">
                          Credits required
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-zinc-500">{selectedModel.endpoint || selectedModel.id}</p>
                  </div>
                  <span className="text-xs px-2 py-1 bg-white/10 rounded-full text-zinc-400">
                    {CATEGORIES.find(c => c.id === activeCategory)?.label}
                  </span>
                </div>

                <div className="space-y-4">
                  {/* Image Upload */}
                  {needsImage && (
                    <div>
                      <label className="text-sm font-medium text-zinc-300 block mb-2">Source Image *</label>
                      {uploadedUrl ? (
                        <div className="relative inline-block">
                          <img src={uploadedUrl.startsWith('http') ? uploadedUrl : `${API}${uploadedUrl}`} alt="uploaded" className="w-32 h-32 object-cover rounded-xl border border-white/10" />
                          <button onClick={() => { setUploadedFile(null); setUploadedUrl(''); }}
                            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                            <X className="w-3 h-3 text-white" />
                          </button>
                        </div>
                      ) : (
                        <div onClick={() => fileInputRef.current?.click()}
                          className="border-2 border-dashed border-white/20 rounded-xl p-8 text-center cursor-pointer hover:border-violet-500/50 hover:bg-violet-500/5 transition-all">
                          {uploading ? <Loader2 className="w-6 h-6 text-violet-400 animate-spin mx-auto" /> : (
                            <><Upload className="w-6 h-6 text-zinc-500 mx-auto mb-2" /><p className="text-sm text-zinc-500">Click to upload</p></>
                          )}
                        </div>
                      )}
                      <input ref={fileInputRef} type="file" accept="image/*" className="hidden"
                        onChange={e => e.target.files[0] && handleFileUpload(e.target.files[0])} />
                    </div>
                  )}

                  {/* Prompt */}
                  {(!needsImage || activeCategory !== 'image_to_image' || selectedModel?.hasPrompt !== false) && (
                    <div>
                      <label className="text-sm font-medium text-zinc-300 block mb-2">
                        {isAudioOutput ? 'Text / Lyrics' : isI2V ? 'Motion Prompt *' : needsImage ? 'Instructions (optional)' : 'Prompt'} {!needsImage && !isI2V ? '*' : ''}
                      </label>
                      <textarea
                        data-testid="prompt-input"
                        value={prompt}
                        onChange={e => setPrompt(e.target.value)}
                        placeholder={
                          activeCategory === 'text_to_image' ? 'A cinematic portrait of a futuristic city at dusk, dramatic lighting...' :
                          activeCategory === 'text_to_video' ? 'A timelapse of cherry blossoms falling in slow motion...' :
                          activeCategory === 'image_to_image' ? 'Make the background a sunset beach...' :
                          activeCategory === 'image_to_video' ? 'Describe the motion: slow zoom in, camera pan left, character walks forward...' :
                          'Enter your text or lyrics to generate audio...'
                        }
                        className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-violet-500/50 resize-none"
                        rows={3}
                      />
                    </div>
                  )}

                  {/* Options */}
                  <div className="flex flex-wrap gap-3">
                    {['text_to_image', 'image_to_image', 'text_to_video', 'image_to_video'].includes(activeCategory) && (
                      <div>
                        <label className="text-xs text-zinc-500 block mb-1">Aspect Ratio</label>
                        <select value={aspectRatio} onChange={e => setAspectRatio(e.target.value)}
                          className="bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none">
                          {(isI2V ? I2V_ASPECT_RATIOS : ASPECT_RATIOS).map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </div>
                    )}
                    {isVideoOutput && (
                      <div>
                        <label className="text-xs text-zinc-500 block mb-1">Duration</label>
                        <select value={duration} onChange={e => setDuration(e.target.value)}
                          className="bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none">
                          {(isI2V ? I2V_DURATIONS : ['5','8','10','16']).map(d => <option key={d} value={d}>{d}s</option>)}
                        </select>
                      </div>
                    )}
                    {/* Suno music style */}
                    {activeCategory === 'text_to_audio' && (selectedModel?.inputs || []).includes('style') && (
                      <div>
                        <label className="text-xs text-zinc-500 block mb-1">Music Style</label>
                        <select value={musicStyle} onChange={e => setMusicStyle(e.target.value)}
                          className="bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none">
                          {SUNO_STYLES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                    )}
                    {/* Minimax voice selector */}
                    {activeCategory === 'text_to_audio' && (selectedModel?.inputs || []).includes('voice_id') && (
                      <div>
                        <label className="text-xs text-zinc-500 block mb-1">Voice</label>
                        <select value={voiceId} onChange={e => setVoiceId(e.target.value)}
                          className="bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none">
                          {MINIMAX_VOICES.map(v => <option key={v} value={v}>{v.replace(/_/g,' ')}</option>)}
                        </select>
                      </div>
                    )}
                  </div>

                  {/* Generate Button */}
                  <Button
                    data-testid="generate-button"
                    onClick={handleGenerate}
                    disabled={generating || uploading}
                    className={`w-full font-semibold py-3 rounded-xl ${
                      isFreeModel
                        ? 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500'
                        : 'bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500'
                    } text-white`}
                  >
                    {generating ? (
                      <span className="flex items-center gap-2 justify-center">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {pollStatus === 'submitting' ? 'Submitting...' : 'Generating...'}
                      </span>
                    ) : isFreeModel ? (
                      <span className="flex items-center gap-2 justify-center"><Sparkles className="w-4 h-4" /> Generate Free</span>
                    ) : 'Generate'}
                  </Button>

                  {!isFreeModel && (
                    <p className="text-xs text-zinc-600 text-center">This model requires muapi.ai credits. Top up at muapi.ai</p>
                  )}

                  {jobId && generating && (
                    <div className="flex items-center gap-2 text-xs text-zinc-500">
                      <Clock className="w-3 h-3" />
                      <span>Job: {jobId.slice(0, 12)}... · Polling every 3s</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Error */}
            {error && !generating && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-5 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-red-300">Generation Failed</p>
                  <p className="text-xs text-red-400 mt-1">{error}</p>
                </div>
              </div>
            )}

            {/* Result */}
            {result && !generating && resultUrl && (
              <div className="bg-zinc-900/50 rounded-2xl border border-white/5 p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-zinc-300">Result</p>
                  <Button size="sm" variant="outline" onClick={() => { setResult(null); setError(null); }}
                    className="text-xs border-white/10 text-zinc-400 hover:text-white">
                    <RefreshCw className="w-3 h-3 mr-1" /> New
                  </Button>
                </div>
                {isVideoOutput ? (
                  <video data-testid="result-video" controls className="w-full rounded-xl" src={resultUrl} />
                ) : isAudioOutput ? (
                  <audio data-testid="result-audio" controls className="w-full" src={resultUrl} />
                ) : (
                  <img data-testid="result-image" src={resultUrl} alt="Generated" className="w-full rounded-xl" />
                )}
                <Button data-testid="download-result" onClick={() => downloadResult(result.output_url)}
                  className="w-full bg-white/10 hover:bg-white/20 text-white border border-white/10">
                  <Download className="w-4 h-4 mr-2" /> Download
                </Button>
              </div>
            )}

            {!selectedModel && (
              <div className="text-center py-16 text-zinc-600">
                <Zap className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p>Select a model to start generating</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
